/**
 * The ranked screen's runtime: three fetches, and the state they land in.
 *
 * The same split every screen in this directory makes. `MenuModel` decides
 * which mode is being looked at and when a refresh is due; this holds the
 * answers and nothing else; ./ranked-panels turns them into markup and is
 * pure. Nothing here touches an element.
 *
 * ===========================================================================
 * A STALE ANSWER IS WORSE THAN A LATE ONE
 * ===========================================================================
 *
 * The leaderboard is per mode and the row that changes it is a cycler, so a
 * player holding right walks through four modes in half a second and starts
 * four requests. They can land in any order, and the last one to arrive is not
 * the one that was asked for last. So every request carries a sequence number
 * and an answer that is not the newest is dropped — the alternative is a board
 * labelled 3v3 showing the 1v1 ladder, which looks exactly like a ranking bug.
 *
 * Failures are kept as a SENTENCE rather than thrown. A ladder that cannot be
 * reached is an ordinary thing on a game that can be played offline, and the
 * screen says so where the board would be.
 */

import type { AccountApi, LeaderboardEntry, RankedMatch } from '../game/account';
import { emptyRating, type RatingRecord } from '../game/rating';

/** Rows of ladder fetched at once. Enough to scroll, small enough to read. */
export const LEADERBOARD_PAGE = 20;
/** Matches shown in the history. */
export const HISTORY_PAGE = 10;

export interface RankedView {
  readonly ratings: readonly RatingRecord[];
  readonly entries: readonly LeaderboardEntry[];
  readonly matches: readonly RankedMatch[];
  readonly mode: string;
  readonly userId: number | null;
  readonly loading: boolean;
  readonly error: string | null;
}

export interface RankedOptions {
  api: AccountApi;
  /** The live login token, or null for a guest. Read per call. */
  token: () => string | null;
  /** The signed-in user's id, for highlighting their row. */
  userId: () => number | null;
  /** Called whenever the view changed and the screen should be redrawn. */
  onChanged: () => void;
}

export class RankedRuntime {
  private ratings: RatingRecord[] = [];
  private entries: LeaderboardEntry[] = [];
  private matches: RankedMatch[] = [];
  private mode = 'duel';
  private loading = false;
  private error: string | null = null;
  /** Bumped per refresh; an answer from an older one is dropped. */
  private sequence = 0;

  constructor(private readonly options: RankedOptions) {}

  view(): RankedView {
    return {
      ratings: this.ratings,
      entries: this.entries,
      matches: this.matches,
      mode: this.mode,
      userId: this.options.userId(),
      loading: this.loading,
      error: this.error,
    };
  }

  /** The player's standing in one mode, whether or not they have played it. */
  standing(mode: string): RatingRecord {
    return this.ratings.find(r => r.mode === mode) ?? emptyRating(mode);
  }

  /**
   * Re-read everything for `mode`.
   *
   * The board is fetched for a GUEST too — it is public, and a signed-out
   * player looking at the ladder is exactly who it is there to interest. Only
   * the two personal calls need a token, and their absence is not an error.
   */
  refresh(mode: string): void {
    this.mode = mode;
    const seq = ++this.sequence;
    this.loading = true;
    this.error = null;
    this.options.onChanged();

    const token = this.options.token();
    if (token === null) {
      this.ratings = [];
      this.matches = [];
    }

    void Promise.all([
      this.options.api.leaderboard(mode, LEADERBOARD_PAGE),
      token === null ? null : this.options.api.ranked(token),
      token === null ? null : this.options.api.rankedHistory(token, HISTORY_PAGE),
    ]).then(([board, standing, history]) => {
      if (seq !== this.sequence) return;
      this.loading = false;

      if (board.ok) this.entries = board.value.entries;
      else this.error = board.error;

      if (standing !== null && standing.ok) this.ratings = standing.value.ratings;
      if (history !== null && history.ok) this.matches = history.value.matches;

      this.options.onChanged();
    });
  }

  /** Drop everything. Called on sign-out, so one account's ladder standing and
   *  history do not survive into the next account's screen. */
  clear(): void {
    this.sequence++;
    this.ratings = [];
    this.matches = [];
    this.entries = [];
    this.loading = false;
    this.error = null;
  }
}
